import { emptyLedger, type LocalLedger } from "../src/ledger";
import { normalizeSyncLedger, parseSyncCommand, type LedgerSyncCommand, type LedgerSyncResult, type LedgerSyncState } from "../src/sync/protocol";

export type SyncPhase = "synced" | "syncing" | "local" | "conflict" | "error";

export interface SyncStatus {
  phase: SyncPhase;
  detail: string;
}

export interface SyncTransport {
  pull(): Promise<LedgerSyncState>;
  push(command: LedgerSyncCommand): Promise<LedgerSyncResult>;
}

export interface SyncStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const deviceKey = "time-tile.sync.v1.device";
const revisionKey = "time-tile.sync.v1.revision";
const pendingKey = "time-tile.sync.v1.pending";
const desiredKey = "time-tile.sync.v1.desired";
const recoveryPrefix = "time-tile.sync.v1.recovery";

function parsed<T>(storage: SyncStorage, key: string): T | null {
  const raw = storage.getItem(key);
  if (!raw) return null;
  try { return JSON.parse(raw) as T; } catch { return null; }
}

function equalLedger(left: LocalLedger, right: LocalLedger): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function hasUserLedgerState(ledger: LocalLedger): boolean {
  if (ledger.running || ledger.completed.length || ledger.nudgeEvents.length) return true;
  const baseline = emptyLedger(0);
  if (JSON.stringify(ledger.nudgePolicy) !== JSON.stringify(baseline.nudgePolicy)) return true;
  if (ledger.tags.length !== baseline.tags.length) return true;
  return ledger.tags.some((tag) => {
    const initial = baseline.tags.find((candidate) => candidate.id === tag.id);
    return !initial || tag.label !== initial.label || tag.color !== initial.color || tag.isFavorite !== initial.isFavorite || tag.defaultActivityMode !== initial.defaultActivityMode || Boolean(tag.archivedAt);
  });
}

function validState(value: unknown): LedgerSyncState | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<LedgerSyncState>;
  if (typeof candidate.revision !== "number" || !Number.isSafeInteger(candidate.revision) || candidate.revision < 0) return null;
  if (candidate.ledger === null && candidate.revision === 0) return { revision: 0, ledger: null };
  const ledger = normalizeSyncLedger(candidate.ledger);
  return ledger ? { revision: candidate.revision, ledger } : null;
}

function validResult(value: unknown): LedgerSyncResult | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<LedgerSyncResult>;
  if (!new Set(["applied", "duplicate", "conflict"]).has(String(candidate.status))) return null;
  const state = validState({ revision: candidate.revision, ledger: candidate.ledger });
  return state?.ledger ? { status: candidate.status as LedgerSyncResult["status"], revision: state.revision, ledger: state.ledger } as LedgerSyncResult : null;
}

export class HttpSyncTransport implements SyncTransport {
  async pull(): Promise<LedgerSyncState> {
    const response = await fetch("/api/sync/ledger", { credentials: "same-origin", headers: { accept: "application/json" } });
    if (!response.ok) throw new Error(response.status === 401 ? "Time Tile needs to be unlocked again." : "Could not reach the shared timer.");
    const state = validState(await response.json().catch(() => null));
    if (!state) throw new Error("The shared timer returned an invalid response.");
    return state;
  }

  async push(command: LedgerSyncCommand): Promise<LedgerSyncResult> {
    const response = await fetch("/api/sync/commands", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(command),
    });
    if (!response.ok && response.status !== 409) throw new Error(response.status === 401 ? "Time Tile needs to be unlocked again." : "Could not update the shared timer.");
    const result = validResult(await response.json().catch(() => null));
    if (!result) throw new Error("The shared timer returned an invalid response.");
    return result;
  }
}

export class LedgerSyncClient {
  private current: LocalLedger;
  private chain: Promise<void> = Promise.resolve();
  private enabled = true;

  constructor(
    initial: LocalLedger,
    private readonly storage: SyncStorage,
    private readonly transport: SyncTransport,
    private readonly onLedger: (ledger: LocalLedger) => void,
    private readonly onStatus: (status: SyncStatus) => void,
    private readonly now: () => Date = () => new Date(),
    private readonly createId: () => string = () => crypto.randomUUID(),
  ) {
    this.current = initial;
  }

  initialize(): Promise<void> {
    return this.enqueue(async () => {
      if (this.pending() || this.desired()) {
        const drained = await this.drain();
        if (!drained) return;
      }
      await this.pull();
    });
  }

  commit(ledger: LocalLedger): void {
    const normalized = normalizeSyncLedger(ledger);
    if (!normalized) {
      this.onStatus({ phase: "error", detail: "This timer could not be prepared for sync." });
      return;
    }
    this.current = normalized;
    this.storage.setItem(desiredKey, JSON.stringify(normalized));
    this.onStatus({ phase: "local", detail: "Saved on this device. Syncing when available." });
    void this.enqueue(async () => { await this.drain(); });
  }

  refresh(): Promise<void> {
    return this.enqueue(async () => {
      const drained = await this.drain();
      if (drained) await this.pull();
    });
  }

  stop(): void {
    this.enabled = false;
  }

  private enqueue(task: () => Promise<void>): Promise<void> {
    const next = this.chain.then(async () => { if (this.enabled) await task(); });
    this.chain = next.catch(() => undefined);
    return next;
  }

  private revision(): number {
    const value = Number(this.storage.getItem(revisionKey));
    return Number.isSafeInteger(value) && value >= 0 ? value : 0;
  }

  private setRevision(revision: number): void {
    this.storage.setItem(revisionKey, String(revision));
  }

  private deviceId(): string {
    const existing = this.storage.getItem(deviceKey);
    if (existing) return existing;
    const created = this.createId();
    this.storage.setItem(deviceKey, created);
    return created;
  }

  private pending(): LedgerSyncCommand | null {
    const command = parsed<unknown>(this.storage, pendingKey);
    const normalized = parseSyncCommand(command);
    if (!normalized && command) this.storage.removeItem(pendingKey);
    return normalized;
  }

  private desired(): LocalLedger | null {
    const ledger = normalizeSyncLedger(parsed(this.storage, desiredKey));
    if (!ledger && this.storage.getItem(desiredKey)) this.storage.removeItem(desiredKey);
    return ledger;
  }

  private createPending(ledger: LocalLedger): LedgerSyncCommand {
    const command: LedgerSyncCommand = {
      commandId: this.createId(),
      deviceId: this.deviceId(),
      baseRevision: this.revision(),
      issuedAt: this.now().toISOString(),
      ledger,
    };
    this.storage.setItem(pendingKey, JSON.stringify(command));
    this.storage.removeItem(desiredKey);
    return command;
  }

  private preserveRecovery(ledger: LocalLedger): void {
    this.storage.setItem(`${recoveryPrefix}.${this.now().getTime()}`, JSON.stringify(ledger));
  }

  private applyRemote(ledger: LocalLedger): void {
    if (equalLedger(this.current, ledger)) return;
    this.current = ledger;
    this.onLedger(ledger);
  }

  private async drain(): Promise<boolean> {
    while (this.enabled) {
      let pending = this.pending();
      if (!pending) {
        const desired = this.desired();
        if (!desired) {
          this.onStatus({ phase: "synced", detail: "Timer is synchronized across your devices." });
          return true;
        }
        pending = this.createPending(desired);
      }
      this.onStatus({ phase: "syncing", detail: "Synchronizing timer…" });
      let result: LedgerSyncResult;
      try {
        result = await this.transport.push(pending);
      } catch (error) {
        this.onStatus({ phase: "local", detail: error instanceof Error ? error.message : "Saved on this device." });
        return false;
      }
      if (result.status === "conflict") {
        this.preserveRecovery(this.desired() ?? pending.ledger);
        this.storage.removeItem(pendingKey);
        this.storage.removeItem(desiredKey);
        this.setRevision(result.revision);
        this.applyRemote(result.ledger);
        this.onStatus({ phase: "conflict", detail: "Another device changed the timer. Its newer version is shown; your local version was preserved for recovery." });
        return false;
      }
      this.storage.removeItem(pendingKey);
      this.setRevision(result.revision);
      if (!this.desired()) this.applyRemote(result.ledger);
    }
    return false;
  }

  private async pull(): Promise<void> {
    this.onStatus({ phase: "syncing", detail: "Checking the shared timer…" });
    let state: LedgerSyncState;
    try {
      state = await this.transport.pull();
    } catch (error) {
      this.onStatus({ phase: "local", detail: error instanceof Error ? error.message : "Using the timer saved on this device." });
      return;
    }
    if (this.pending() || this.desired()) {
      await this.drain();
      return;
    }
    if (!state.ledger) {
      this.storage.setItem(desiredKey, JSON.stringify(this.current));
      await this.drain();
      return;
    }
    const firstCloudRead = this.storage.getItem(revisionKey) === null;
    if (firstCloudRead && hasUserLedgerState(this.current) && !hasUserLedgerState(state.ledger)) {
      this.setRevision(state.revision);
      this.storage.setItem(desiredKey, JSON.stringify(this.current));
      await this.drain();
      return;
    }
    if (firstCloudRead && hasUserLedgerState(this.current) && !equalLedger(this.current, state.ledger)) this.preserveRecovery(this.current);
    this.setRevision(state.revision);
    this.applyRemote(state.ledger);
    this.onStatus({ phase: "synced", detail: "Timer is synchronized across your devices." });
  }
}
